import { ref, computed } from "vue";
import { useCurriculumStore } from "@/stores/curriculumStore";
import { useProgressStore } from "@/stores/progressStore";
import { useUserStore } from "@/stores/userStore";
import { useQuestionBank } from "@/composables/useQuestionBank";
import { LessonSelectionPolicy } from "@/policies/lessonSelectionPolicy";
import { useDailyStreakEngine } from "@/composables/useDailyStreakEngine";
import { isLessonLocked } from "@/composables/usePlanGating";
import { useAnalytics } from "@/composables/useAnalytics";

/**
 * ==============================
 * CONFIG
 * ==============================
 */

// Soft fatigue guard — NOT a completion condition
const MAX_QUESTIONS_PER_SESSION = 10;

// Minimum questions before completion can trigger
const MIN_QUESTIONS_FOR_COMPLETION = 3;

// How recently we resume a lesson automatically
const RESUME_WINDOW_DAYS = 2;

// Confidence bands (order matters)
const BAND_ORDER = ["beginner", "developing", "secure", "confident", "mastery"];

/**
 * ==============================
 * HELPERS
 * ==============================
 */

function bandIndex(b) {
  return Math.max(0, BAND_ORDER.indexOf(b));
}

function bandMin(a, b) {
  return bandIndex(a) <= bandIndex(b) ? a : b;
}

function clamp01(n) {
  return Math.max(0, Math.min(1, n));
}

function clampDifficulty(d) {
  const x = (d ?? "").toString().trim().toLowerCase();
  if (x === "easy" || x === "medium" || x === "hard") return x;
  return "medium";
}
function bumpUp(d) {
  d = clampDifficulty(d);
  if (d === "easy") return "medium";
  if (d === "medium") return "hard";
  return "hard";
}
function bumpDown(d) {
  d = clampDifficulty(d);
  if (d === "hard") return "medium";
  if (d === "medium") return "easy";
  return "easy";
}

function daysToMs(d) {
  return d * 24 * 60 * 60 * 1000;
}

function confidenceScoreFromBand(band) {
  return {
    beginner: 0.25,
    developing: 0.45,
    secure: 0.65,
    confident: 0.8,
    mastery: 0.92
  }[band] ?? 0.45;
}

// Weighting for SG selection
function weightForBand(band, seenThisSession) {
  if (!seenThisSession) return 8; // unseen SGs first
  return {
    beginner: 6,
    developing: 4,
    secure: 2,
    confident: 1,
    mastery: 0.5
  }[band] ?? 3;
}

/**
 * ==============================
 * ENGINE
 * ==============================
 */

export function useContinueLearningEngine(childId) {
  const curriculumStore = useCurriculumStore();
  const progressStore = useProgressStore();
  const userStore = useUserStore();
  const questionBank = useQuestionBank();
  const streakEngine = useDailyStreakEngine();
  const { track } = useAnalytics();

  /**
   * ------------------------------
   * SESSION STATE
   * ------------------------------
   */

  const sessionStarted = ref(false);
  const sessionCompleted = ref(false);
  const sessionPaused = ref(false);
  const forcePracticeLesson = ref(false);

  // Loading and error states
  const isLoading = ref(false);
  const noQuestionsAvailable = ref(false);
  const questionsExhausted = ref(false); // All questions used but mastery not achieved

  // Focus on a single section group (for topic-specific practice)
  const focusedSectionGroupId = ref(null);

  // Explicit flag: true when the session was started in Practice All mode.
  // Decoupled from focusedSectionGroupId so the correct completion branch always
  // runs even if focusedSectionGroupId is set to a non-null value by mistake.
  const sessionPracticeAll = ref(false);

  const lessonReady = ref(false);
  const lesson = ref(null);

  const queue = ref([]); // [{ sectionGroupId, question }]
  const currentIndex = ref(0);

  const questionsThisSession = ref(0);

  const lastDifficulty = ref(null);


  // SG coverage tracking (this session)
  const sessionSeenBySG = ref({});
  const sessionCorrectBySG = ref({});

  // UI outputs
  const adaptiveMessage = ref(null);
  const lessonBand = ref("developing");
  const completionType = ref(null); // "topic" or "lesson"


  /**
   * ------------------------------
   * COMPUTEDS
   * ------------------------------
   */

  const currentItem = computed(() => queue.value[currentIndex.value] || null);

  const currentQuestion = computed(() => {
    if (!currentItem.value) return null;
    return {
      sectionGroupId: currentItem.value.sectionGroupId,
      difficulty: currentItem.value.question?.difficulty,
      question: currentItem.value.question
    };
  });

  const currentSectionGroupId = computed(() => currentItem.value?.sectionGroupId || null);

  const currentTargetDifficulty = computed(() => {
    const sgId = currentSectionGroupId.value;
    if (!sgId) return "medium";
    return clampDifficulty(targetDifficultyBySG.value[sgId]);
  });

  const currentQuestionDifficulty = computed(() => {
    const d = currentItem.value?.question?.difficulty;
    return d ? clampDifficulty(d) : null;
  });

  // what UI should display (prefer actual question difficulty, fallback to target)
  const displayDifficulty = computed(() => {
    return currentQuestionDifficulty.value || currentTargetDifficulty.value || "medium";
  });

  // Curriculum helpers — locked lessons are invisible to the engine
  const allLessons = computed(() =>
    (curriculumStore.allLessons || []).filter(
      l => !isLessonLocked(l.excludedPlans, userStore.effectivePlan)
    )
  );

  const suggestedNextLesson = computed(() => { 
    const res = selectNextLesson();
    if (!res?.lesson) return null;

    return {
      lessonId: res.lesson.id,
      lessonTitle: res.lesson.title,
      unitTitle: res.lesson.unitTitle,
      moduleTitle: res.lesson.moduleTitle,
      reason: explainLessonChoice(res.reasonCode)
    };
  });

  /**
   * ------------------------------
   * LESSON SELECTION
   * ------------------------------
   */

  function findLessonById(id) {
    return allLessons.value.find(l => l.id === id) || null;
  }

  function activateLesson({
    childId,
    lessonId
  }) {
    progressStore.setContinueLesson(childId, lessonId);
    // progressStore.clearContinueLesson(childId);
    // progressStore.clearOverrideLesson(childId);
  }

  function selectNextLesson() {
    // ❌ DO NOT mutate stores here
    // ❌ DO NOT clear continueState
    // ❌ DO NOT set lesson state

    /* -----------------------------
      1. Manual override still wins
    ------------------------------ */
    const overrideId = progressStore.getOverrideLesson?.(childId);
    if (overrideId) {
      const lesson = findLessonById(overrideId);
      if (lesson) {
        return {
          lesson,
          reasonCode: "manual_override"
        };
      }
    }

    // 🔁 FORCE SAME LESSON FOR PRACTICE
    if (forcePracticeLesson.value && lesson.value) {
      return {
        lesson: lesson.value,
        reasonCode: "manual_override",
        reasonData: {}
      }
    }

    /* ---------------------------------
     1. Resume paused lesson (only if it's not already complete)
    ---------------------------------- */
    const resume = progressStore.getContinueLesson(childId);
    if (resume?.lessonId && resume.updatedAt) {
      const recent = Date.now() - resume.updatedAt <= daysToMs(RESUME_WINDOW_DAYS);
      if (recent) {
        const l = findLessonById(resume.lessonId);
        if (l) {
          const readiness = getLessonReadiness(childId, l);
          if (!readiness.ready) {
            return {
              lesson: l,
              reasonCode: "resume",
              reasonData: {}
            };
          }
          // Lesson is already complete — clear the stale continue pointer
          progressStore.clearContinueLesson(childId);
        }
      }
    }

    /* ---------------------------------
     2. Decide selection mode
    ---------------------------------- */
    const mode = determineSelectionMode();

    /* ---------------------------------
     3. Curriculum-guided selection
    ---------------------------------- */
    if (mode === "curriculum") {  
      // Curriculum order is preserved by curriculumStore
      for (const lesson of allLessons.value) {
        const readiness = getLessonReadiness(childId, lesson);
        if (!readiness.ready) {
          return {
            lesson,
            reasonCode: "curriculum_next",
            reasonData: {}
          }
        }
      }
    // All lessons ready → fall through to adaptive
    }

    /* ---------------------------------
     4. Adaptive reinforcement
    ---------------------------------- */

    let weakestLesson = null;
    let weakestScore = Infinity;

    for (const lesson of allLessons.value) {
      let score = 0;
      let count = 0;

      for (const subsection of lesson.subsections || []) {
        score += bandIndex(progressStore.getSectionGroupBand(childId, subsection.name));
        count += 1;
      }

      const avg = count ? score / count : Infinity;

      if (avg < weakestScore) {
        weakestScore = avg;
        weakestLesson = lesson;
      }
    }

    return {
      lesson: weakestLesson || allLessons.value[0] || null,
      reasonCode: "reinforcement",
      reasonData: {}
    }
  }

  /**
   * ------------------------------
   * SESSION INITIALISATION
   * ------------------------------
   */

  function resetSession() {
    sessionStarted.value = false;
    sessionCompleted.value = false;
    sessionPaused.value = false;
    lessonReady.value = false;
    lastDifficulty.value = null;
    adaptiveMessage.value = null;
    focusedSectionGroupId.value = null;
    sessionPracticeAll.value = false;

    // Reset loading states
    isLoading.value = false;
    noQuestionsAvailable.value = false;
    questionsExhausted.value = false;

    lesson.value = null;
    queue.value = [];
    currentIndex.value = 0;

    // Reset question bank session (clears used question tracking)
    questionBank.resetSession();
    questionsThisSession.value = 0;

    sessionSeenBySG.value = {};
    sessionCorrectBySG.value = {};

    adaptiveMessage.value = null;
    lessonBand.value = "developing";
    completionType.value = null;
  }

  function initSGMaps(lessonObj) {
    const subs = lessonObj.subsections || [];
    const seen = {};
    const corr = {};
    for (const sub of subs) {
      // Pre-mark SGs already at secure+ as seen so the coverage gate doesn't
      // force questions on topics the student has already mastered.
      const band = progressStore.getSectionGroupBand(childId, sub.content);
      seen[sub.content] = bandIndex(band) >= bandIndex("secure") ? 1 : 0;
      corr[sub.content] = 0;
    }
    sessionSeenBySG.value = seen;
    sessionCorrectBySG.value = corr;
  }

  function bandIndex(band) {
    return BAND_ORDER.indexOf(band);
  }

  function getLessonBand(childId, lesson) {
    const confidencePercent = getLessonConfidence(childId, lesson);

    if (confidencePercent < 35) return "beginner";
    if (confidencePercent < 55) return "developing";
    if (confidencePercent < 72) return "secure";
    if (confidencePercent < 86) return "confident";
    return "mastery";
  }

  /**
   * Unified lesson readiness and analytics
   * @param {string} childId
   * @param {object} lessonObj
   * @param {object} opts - { exhausted, targets, sessionSeenBySG }
   * @returns {object} { ready, band, coverage, strengths, growth }
   */
  function getLessonReadiness(childId, lessonObj, opts = {}) {
    const subs = lessonObj?.subsections || [];
    const sgIds = subs.map(sub => sub.content);
    if (!sgIds.length) return { ready: false, band: null, coverage: 0, strengths: [], growth: [] };

    const targets = opts.targets || getLessonTargets(lessonObj);
    let band = "mastery";
    let ready = true;
    let metCount = 0;

    for (const sg of sgIds) {
      const sgBand = progressStore.getSectionGroupBand(childId, sg);
      band = bandMin(band, sgBand);
      if (bandIndex(sgBand) >= bandIndex(targets[sg])) {
        metCount += 1;
      } else {
        ready = false;
      }
    }

    // Use sessionSeenBySG if provided, else default to all seen
    const seenMap = opts.sessionSeenBySG || {};
    const coverage = clamp01(
      sgIds.filter(sg => (seenMap[sg] || 0) > 0).length / sgIds.length
    );

    if (coverage < 1) band = bandMin(band, "secure");
    if (opts.exhausted) band = bandMin(band, "confident");

    const strengths = sgIds.filter(
      sg => bandIndex(progressStore.getSectionGroupBand(childId, sg)) >= bandIndex("confident")
    );

    return { ready, band, coverage, strengths };
  }

  function explainLessonChoice(reasonCode) {
    return {
      resume: {
        title: "You were working on this lesson",
        text: "We’re continuing from where you last stopped so learning stays smooth."
      },
      curriculum_next: {
        title: "Following the curriculum",
        text: "This lesson follows naturally from what has already been covered."
      },
      reinforcement: {
        title: "Extra practice recommended",
        text: "Some skills in this lesson need a bit more confidence, so it’s a good focus right now."
      },
      manual_override: {
        title: "Selected lesson",
        text: "This lesson was selected manually to focus on a specific area."
      }
    }[reasonCode];
  }

  function determineSelectionMode() {
    const lessons = allLessons.value || [];
    if (!lessons.length) return LessonSelectionPolicy.DEFAULT_MODE;

    const completed = lessons.filter((l) =>
      getLessonReadiness(childId, l).ready
    );

    const completedRatio = completed.length / lessons.length;

    // Average confidence across curriculum
    let total = 0;
    let count = 0;

    for (const l of lessons) {
      for (const sub of l.subsections || []) {
        total += bandIndex(progressStore.getSectionGroupBand(childId, sub.content));
        count += 1;
      }
    }

    const avgConfidence = count ? total / count / 4 : 0;

    if (
      completedRatio >= LessonSelectionPolicy.ADAPTIVE_READINESS_RATIO &&
      avgConfidence >= LessonSelectionPolicy.CURRICULUM_CONFIDENCE_THRESHOLD
    ) {
      return "adaptive";
    }

    return "curriculum";
  }


  /**
   * ------------------------------
   * QUESTION SELECTION
   * ------------------------------
   */

  function pickNextSectionGroup() {
    // If focused on single SG, always return it
    if (focusedSectionGroupId.value) {
      return focusedSectionGroupId.value;
    }

    const subs = lesson.value?.subsections || [];
    const sgIds = subs.map(sub => sub.content);
    if (!sgIds.length) return null;

    const weighted = sgIds.map(sgId => {
      const band = progressStore.getSectionGroupBand(childId, sgId);
      const seen = (sessionSeenBySG.value[sgId] || 0) > 0;
      // SGs already at secure+ that have been seen (or pre-marked) get weight 0
      // so Practice All focuses only on topics still needing work.
      const w = (seen && bandIndex(band) >= bandIndex("secure"))
        ? 0
        : weightForBand(band, seen);
      return { sgId, w };
    });

    let total = weighted.reduce((a, x) => a + x.w, 0);
    // Edge case: all SGs are already at secure+ — give equal weight so we can
    // still generate the minimum questions required for session completion.
    if (total === 0) {
      weighted.forEach(x => { x.w = 1; });
      total = weighted.length;
    }
    let r = Math.random() * total;
    for (const x of weighted) {
      r -= x.w;
      if (r <= 0) return x.sgId;
    }
    return weighted[weighted.length - 1].sgId;
  }

  async function appendNextQuestion() {
    // Build candidate list: in practice-all mode try the weighted pick first,
    // then fall back to ALL other SGs so one exhausted topic doesn't end the session.
    let candidates;
    if (focusedSectionGroupId.value) {
      candidates = [focusedSectionGroupId.value];
    } else {
      const picked = pickNextSectionGroup();
      if (!picked) return false;
      const allSgIds = (lesson.value?.subsections || []).map(sub => sub.content);
      const others = allSgIds.filter(id => id !== picked);
      // Prioritise not-yet-secure SGs as fallbacks so practice focusses on topics
      // that still need work. Already-secure SGs come last as a last resort.
      const notSecure = others.filter(
        id => bandIndex(progressStore.getSectionGroupBand(childId, id)) < bandIndex("secure")
      );
      const alreadySecure = others.filter(
        id => bandIndex(progressStore.getSectionGroupBand(childId, id)) >= bandIndex("secure")
      );
      candidates = [picked, ...notSecure, ...alreadySecure];
    }

    let sgId = null;
    let q = null;
    let nextDifficulty = "medium";

    for (const candidate of candidates) {
      const band = progressStore.getSectionGroupBand(childId, candidate);
      const diff =
        band === "beginner" ? "easy" :
        band === "developing" ? "medium" :
        band === "secure" ? "medium" :
        "hard";

      const result = await questionBank.getQuestion(candidate, diff, { cacheOnly: true });
      if (result && result._id) {
        sgId = candidate;
        q = result;
        nextDifficulty = diff;
        break;
      }
    }

    if (!q || !sgId) return false;

    // -------------------------------
  // 🔔 ADAPTIVE MESSAGE LOGIC
  // -------------------------------
  if (lastDifficulty.value && lastDifficulty.value !== nextDifficulty) {
    if (
      (lastDifficulty.value === "easy" && nextDifficulty === "medium") ||
      (lastDifficulty.value === "medium" && nextDifficulty === "hard")
    ) {
      adaptiveMessage.value = "Getting harder!";
    }

    if (
      (lastDifficulty.value === "hard" && nextDifficulty === "medium") ||
      (lastDifficulty.value === "medium" && nextDifficulty === "easy")
    ) {
      adaptiveMessage.value = "Let's slow down";
    }

    // auto-clear after a moment so it doesn’t stick
    setTimeout(() => {
      adaptiveMessage.value = null;
    }, 1800);
  }

    lastDifficulty.value = nextDifficulty;

    queue.value.push({ sectionGroupId: sgId, question: {
      ...q,
      sgDifficulty: nextDifficulty }
    });
    sessionSeenBySG.value[sgId] += 1;

    return true;
  }

  /**
   * ------------------------------
   * GOAL-BASED READINESS
   * ------------------------------
   */

  function getLessonTargets(lessonObj) {
    const targets = {};
    for (const sub of lessonObj.subsections || []) {
      targets[sub.content] = "secure"; // default goal
    }
    return targets;
  }

  // Unified readiness evaluation for session
  function evaluateLessonReadinessUnified({ exhausted = false } = {}) {
    if (forcePracticeLesson.value) {
      lessonReady.value = false;
      return;
    }

    // Must answer minimum questions before completion can trigger
    if (questionsThisSession.value < MIN_QUESTIONS_FOR_COMPLETION) {
      lessonReady.value = false;
      return;
    }

    let topicReady = false;
    let lessonAlsoReady = false;

    // Single-topic practice: check if focused topic is ready.
    // The !sessionPracticeAll guard ensures Practice All sessions never fall into
    // this branch even if focusedSectionGroupId is non-null due to a state leak.
    if (focusedSectionGroupId.value && !sessionPracticeAll.value) {
      const band = progressStore.getSectionGroupBand(childId, focusedSectionGroupId.value);
      topicReady = bandIndex(band) >= bandIndex("secure");

      // Also check if completing this topic makes the entire lesson ready
      if (topicReady) {
        const lessonResult = getLessonReadiness(childId, lesson.value, { exhausted, sessionSeenBySG: sessionSeenBySG.value });
        lessonAlsoReady = lessonResult.ready;
      }
    } else {
      // Practice-all mode: all topics must be at secure AND each seen at least once
      // this session. The coverage gate prevents early exit when topics were already
      // at secure before the session started (e.g. from the skills check).
      const result = getLessonReadiness(childId, lesson.value, { exhausted, sessionSeenBySG: sessionSeenBySG.value });
      topicReady = result.ready && (exhausted || result.coverage >= 1);
    }

    if (!topicReady) {
      lessonReady.value = false;
      return;
    }

    progressStore.clearContinueLesson(childId);
    progressStore.clearOverrideLesson(childId);
    lessonReady.value = true;
    sessionPaused.value = true;

    // If single-topic practice also completed the whole lesson, trigger lesson completion
    if (focusedSectionGroupId.value && lessonAlsoReady) {
      finishLesson({ exhausted, completedFullLesson: true });
    } else {
      finishLesson({ exhausted });
    }
  }

  /**
   * ------------------------------
   * FLOW CONTROL
   * ------------------------------
   */

  async function ensureNext() {
    if (lessonReady.value || sessionPaused.value) return;

    // In practice-all mode, allow more questions so all topics are covered
    const limit = sessionPracticeAll.value
      ? MAX_QUESTIONS_PER_SESSION * 3
      : MAX_QUESTIONS_PER_SESSION;
    if (questionsThisSession.value >= limit) {
      sessionPaused.value = true;
      progressStore.flushLessonProgress(childId);
      return;
    }

    if (currentIndex.value >= queue.value.length) {
      isLoading.value = true;
      let ok = false;
      try {
        ok = await appendNextQuestion();
      } catch (err) {
        console.warn('Failed to load next question:', err);
      } finally {
        isLoading.value = false;
      }

      if (!ok) {
        evaluateLessonReadinessUnified({ exhausted: true });
        // If lesson not ready after exhaustion, mark as exhausted for UI feedback
        if (!lessonReady.value) {
          questionsExhausted.value = true;
        }
        sessionPaused.value = true;
        progressStore.flushLessonProgress(childId);
      }
    }
  }

  async function start() {
    if (sessionStarted.value) return;

    progressStore.ensureChild(childId);
    const resolved = selectNextLesson();
    const chosen = resolved?.lesson || null;
    if (!chosen) {
      sessionStarted.value = true;
      sessionCompleted.value = true;
      noQuestionsAvailable.value = true;
      return;
    }

    resetSession();
    isLoading.value = true;
    lesson.value = chosen;
    initSGMaps(chosen);

    activateLesson({
      childId: childId,
      lessonId: chosen.id
    });

    // Prefetch questions for all section groups in this lesson (1 API call with stratified difficulties)
    const sgIds = (chosen.subsections || []).map(sub => sub.content);
    if (sgIds.length) {
      await questionBank.prefetch(sgIds, { perGroup: 15 });
    }

    sessionStarted.value = true;
    isLoading.value = false;

    // Check if any questions were loaded
    const ok = await appendNextQuestion();
    if (!ok) {
      noQuestionsAvailable.value = true;
    }
  }

  /**
   * Start a practice session for a specific lesson, optionally focused on a single topic
   * @param {string} lessonId - The lesson ID to practice
   * @param {string|null} sectionGroupId - Optional: focus on a single section group
   */
  async function startWithLesson(lessonId, sectionGroupId = null) {
    progressStore.ensureChild(childId);

    const chosen = findLessonById(lessonId);
    if (!chosen) {
      sessionStarted.value = true;
      sessionCompleted.value = true;
      noQuestionsAvailable.value = true;
      return;
    }

    resetSession();
    isLoading.value = true;
    lesson.value = chosen;
    focusedSectionGroupId.value = sectionGroupId || null;
    sessionPracticeAll.value = !sectionGroupId; // true = "Practice All" mode

    initSGMaps(chosen);
    activateLesson({ childId, lessonId: chosen.id });

    // Prefetch questions - only for focused SG if specified
    const sgIds = sectionGroupId
      ? [sectionGroupId]
      : (chosen.subsections || []).map(sub => sub.content);

    if (sgIds.length) {
      await questionBank.prefetch(sgIds, { perGroup: 15 });
    }

    sessionStarted.value = true;
    isLoading.value = false;

    track('lesson_started', {
      lesson_id: chosen.id,
      lesson_title: chosen.title,
      unit_title: chosen.unitTitle,
      module_title: chosen.moduleTitle,
      mode: sectionGroupId ? 'topic' : 'practice_all',
    });

    // Check if any questions were loaded
    const ok = await appendNextQuestion();
    if (!ok) {
      noQuestionsAvailable.value = true;
    }
  }

  async function submitQuestionResult(result) {
    if (sessionCompleted.value || sessionPaused.value) return;

    const item = queue.value[currentIndex.value];
    if (!item) return;

    const sgId = item.sectionGroupId;

    progressStore.updateProgress(childId, sgId, !!result.correct);

    if (result.correct) {
      sessionCorrectBySG.value[sgId] += 1; //todo: check why?
      // Award XP for correct answer
      progressStore.awardXP(childId, 5, "question_correct");
    }

    questionsThisSession.value += 1; //todo: check why?
    currentIndex.value += 1;

    evaluateLessonReadinessUnified();
    await ensureNext();
  }

  function finishLesson({ exhausted = false, completedFullLesson = false } = {}) {
    if (sessionCompleted.value) return;

    let band;

    // Single-topic completion (but NOT if it also completed the full lesson, and NOT in Practice All mode)
    if (focusedSectionGroupId.value && !sessionPracticeAll.value && !completedFullLesson) {
      band = progressStore.getSectionGroupBand(childId, focusedSectionGroupId.value);
      lessonBand.value = band;
      completionType.value = "topic";

      // Log activity for topic completion
      const topicTitle = lesson.value?.subsections?.find(
        s => s.content === focusedSectionGroupId.value
      )?.title || focusedSectionGroupId.value;

      progressStore.addActivity(childId, {
        type: "topic",
        title: `Practiced topic: ${topicTitle}`,
        module: lesson.value.moduleTitle,
        lessonId: lesson.value.id,
        sectionGroupId: focusedSectionGroupId.value
      });

      // Award XP for topic completion (smaller reward)
      progressStore.awardXP(childId, 15, "topic_complete");
    } else {
      // Full-lesson completion (either direct or triggered by last topic)
      const result = getLessonReadiness(childId, lesson.value, { exhausted, sessionSeenBySG: sessionSeenBySG.value });
      band = result.band;
      lessonBand.value = band;
      completionType.value = "lesson";

      progressStore.awardLessonBadge(childId, lesson.value.id, lesson.value.title, band);

      progressStore.addActivity(childId, {
        type: "lesson",
        title: `Completed lesson: ${lesson.value.title}`,
        module: lesson.value.moduleTitle,
        lessonId: lesson.value.id
      });

      // Award XP for lesson completion
      progressStore.awardXP(childId, 25, "lesson_complete");

      track('lesson_completed', {
        lesson_id: lesson.value.id,
        lesson_title: lesson.value.title,
        module_title: lesson.value.moduleTitle,
        band,
        questions_answered: questionsThisSession.value,
        exhausted,
      });
    }

    // Flush all pending progress updates to API now that session is complete
    progressStore.flushLessonProgress(childId);

    streakEngine.registerActivity(childId);

    // clear continue pointer
    progressStore.clearContinueLesson(childId);
    sessionCompleted.value = true;
  }

  function practiseLessonAgain() {
    const prevLesson = lesson.value
    forcePracticeLesson.value = true;
    resetSession();
    lesson.value = prevLesson;
    start();
  }

  /**
   * ------------------------------
   * PUBLIC API
   * ------------------------------
   */

  return {
    // state
    sessionStarted,
    sessionCompleted,
    sessionPaused,
    lessonReady,
    focusedSectionGroupId,
    isLoading,
    noQuestionsAvailable,
    questionsExhausted,

    lesson,
    currentIndex,
    queue,
    suggestedNextLesson,

    // question
    currentQuestion,
    currentSectionGroupId,
    displayDifficulty,

    // completion info
    lessonBand,
    completionType,

    // UI
    adaptiveMessage,

    // actions
    start,
    startWithLesson,
    submitQuestionResult,
    resetSession,
    practiseLessonAgain,
    findLessonById,
    selectNextLesson,
    activateLesson
  };
}
